import React from 'react';
import { PropTypes } from 'prop-types';
import TextInput from './TextInput';
import { generateComponentDocs, generateStoryDocs } from '../../../utils/documentationUtils';

export default {
  title: 'Components/Inputs/TextInput',
  component: TextInput,
  decorators: [
    (Story) => (
      <div className="w-96">
        <Story />
      </div>
    ),
  ],
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `TextInput` component provides a versatile and customizable input field for text, numbers, or other types. It supports validation, error messages, and flexible layouts.\n\n',
      // When & how to use it?
      '\nUse the `TextInput` component for collecting user inputs in forms or filters. It supports configurations like labels, placeholders, validation, and dynamic styling.\n\n',
      // Current Usage
      `\nThe \`TextInput\` component is widely used across different forms and filters in:
      \n- User Registration
      \n- Login Forms
      \n- Data Entry\n\n`,
      // Related Components
      '\n- **[Icons](http://localhost:6006/?path=/docs/components-icons-icons--docs)**: Used for displaying icons like asterisk for required fields.\n\n',
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    label: {
      control: { type: 'text' },
      description: 'The label displayed for the input field.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    name: {
      control: { type: 'text' },
      description: 'The name attribute of the input field, used for form submission.\n\n',
      table: {
        type: { summary: 'string' },
        required: true,
      },
    },
    value: {
      control: { type: 'string' },
      description: 'The current value of the input field.\n\n',
      table: {
        type: { summary: 'string | number | Date' },
        required: false,
      },
    },
    setValue: {
      control: { type: 'function' },
      description: 'Callback function triggered when the input value changes.\n\n',
      table: {
        type: { summary: 'function' },
        required: true,
      },
    },
    errorMessage: {
      control: { type: 'text' },
      description: 'Error message displayed when the input fails validation.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    placeholder: {
      control: { type: 'text' },
      description: 'Placeholder text displayed inside the input field.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    isRequired: {
      control: { type: 'boolean' },
      description: 'Marks the input field as required for form validation.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    isValid: {
      control: { type: 'boolean' },
      description: 'Indicates whether the input value is valid.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
    isLabelBeside: {
      control: { type: 'boolean' },
      description: 'If true, displays the label beside the input field instead of above it.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
      },
    },
  },
};

// Base Template function for stories
const TextInputTemplate = ({
  label, name, errorMessage = '', placeholder, isRequired = false, isValid = true, isLabelBeside = false,
}) => {
  const [value, setValue] = React.useState('');
  return (
    <TextInput
      value={value}
      setValue={setValue}
      label={label}
      name={name}
      errorMessage={errorMessage}
      placeholder={placeholder}
      isRequired={isRequired}
      isValid={isValid}
      isLabelBeside={isLabelBeside}
    />
  );
};

// Binding the base template
const Template = TextInputTemplate.bind({});

// Creating stories by applying different props
export const Default = Template.bind({});
Default.args = {
  label: 'Label',
  name: 'label',
};

Default.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the default configuration of the `TextInput` component.\n\n',
  // Where is it currently used on the product?
  '\nThe `TextInput` component is used in various forms for text entry.\n\n',
);

export const Invalid = Template.bind({});
Invalid.args = {
  label: 'Label',
  name: 'label',
  errorMessage: 'Invalid input',
  isRequired: true,
  isValid: false,
};
export const LabelBeside = Template.bind({});
LabelBeside.args = {
  label: 'Label',
  name: 'label',
  isLabelBeside: true,
};

export const LabelBesideWithErrorMessage = Template.bind({});
LabelBesideWithErrorMessage.args = {
  label: 'Label',
  name: 'label',
  isLabelBeside: true,
  errorMessage: 'Invalid input',
  isRequired: true,
  isValid: false,
};

export const PlaceHolder = Template.bind({});
PlaceHolder.args = {
  label: 'Label',
  name: 'label',
  placeholder: 'Place holder',
};

export const Email = Template.bind({});
Email.args = {
  label: 'Email',
  name: 'email',
  placeholder: 'example@gmail.com',
};

export const Password = Template.bind({});
Password.args = {
  label: 'Password',
  name: 'password',
  placeholder: 'password',
};

TextInputTemplate.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  isValid: PropTypes.bool,
  isLabelBeside: PropTypes.bool,
};
